import type { LucideIcon } from "lucide-react"
import { ListPlus, Play, RotateCcw, Route, Square, Zap } from "lucide-react"
import { makeAutoObservable } from "mobx"
import { combinedSavedActionClientRequestId, savedActionClientRequestId, savedActionIdsFromClientRequestId } from "../../actions/actionRequestId"
import { sessionActionTargetId, sessionActionTargetRange, type SessionActionTargets } from "../../actions/sessionActionTarget"
import type { SavedAction } from "../../actions/actionTypes"
import { savedActionAvailability } from "../../actions/actionVisibility"
import { track } from "../../analytics"
import { supportsSteeringForHarness } from "../../constants"
import { blockedFeatureForTurn, describeBlockedFeature } from "../../domain/nestFeatureSupport"
import type { FollowupSchedule, SessionTurnStartParams, SessionTurnStartResult, TurnInput } from "../../nest/generated"
import { userInputTurnInput } from "../../nest/turnInput"
import { TASK_TOOL_NAMES } from "../../product"
import { withInlineComments } from "../../prompts/inlineComments"
import type { TaskProposal } from "../../persistence/taskProposalStore"
import type { Comment, QueuedTurn, UserInputContext } from "../../types"
import { ulid } from "../../utils/ulid"
import { requestWildLarkMethod, type WildLarkTaskHarness, type WildLarkTaskTurnStartParams } from "../../wildlark/client"
import { acceptOptimisticSteeredInput } from "../SessionThreadModel"
import type { CodeStore } from "../store"
import type { EditorSnapshot, SmartEditorManager } from "./SmartEditorManager"

type TurnDisposition = NonNullable<SessionTurnStartParams["disposition"]>
type SavedActionRunOptions = { confirmed?: boolean; sessionTargets?: SessionActionTargets }

const MAX_COMBINED_ACTIONS = 8
const MAX_COMBINED_ACTION_PROMPT_LENGTH = 50_000
const ACTION_COALESCE_WINDOW_MS = 2_300
const ACTION_ADDITIONAL_INSTRUCTIONS_HEADING = "Additional instructions for this Action sequence:"
const ACTION_ADDITIONAL_INSTRUCTIONS_LABEL_SUFFIX = " · with instructions"
const ACTION_SELECTED_SESSION_TARGETS_HEADING = `Use these exact selected ${TASK_TOOL_NAMES.startSession} targets; use the Action's agent count and roles for each entry:`

function savedActionBatchPrompt(actions: readonly SavedAction[], additionalInput: UserInputContext | null): string {
    let actionPrompt = actions[0]?.prompt ?? ""
    if (actions.length > 1) {
        const steps = actions.map((action, index) => `Step ${index + 1}: ${action.name}\n\n${action.prompt}`).join("\n\n")
        actionPrompt =
            "Run these selected Actions in order. Operation limits such as 'do not integrate' apply only to the step containing them; " +
            "they do not cancel authorization explicitly granted by another selected step. " +
            "Each step must satisfy its prerequisites and safety checks; stop if a step cannot complete safely. " +
            "Explicit user constraints and repository rules apply throughout.\n\n" +
            steps
    }
    const additionalInstructions = additionalInput?.userInput.trim() ?? ""
    return additionalInstructions ? `${actionPrompt}\n\n${ACTION_ADDITIONAL_INSTRUCTIONS_HEADING}\n${additionalInstructions}` : actionPrompt
}

function savedActionBatchLabel(actionNames: readonly string[], hasAdditionalInput: boolean): string {
    const actionLabel = actionNames.join(" → ")
    return hasAdditionalInput ? `${actionLabel}${ACTION_ADDITIONAL_INSTRUCTIONS_LABEL_SUFFIX}` : actionLabel
}

function savedActionWithSessionTargets(action: SavedAction, targets: SessionActionTargets | undefined): SavedAction {
    if (!action.sessionLaunch) return action
    if (!targets || targets.length === 0) throw new Error(`Action "${action.name}" requires an agent selection`)
    const { minTargets, maxTargets } = sessionActionTargetRange(action.sessionLaunch)
    if (targets.length < minTargets) throw new Error(`Action "${action.name}" requires at least ${minTargets} agents`)
    if (targets.length > maxTargets) throw new Error(`Action "${action.name}" accepts at most ${maxTargets} agent${maxTargets === 1 ? "" : "s"}`)
    const targetIds = targets.map(sessionActionTargetId)
    if (new Set(targetIds).size !== targets.length) throw new Error(`Action "${action.name}" requires unique agent selections`)
    return {
        ...action,
        prompt: `${action.prompt}\n\n${ACTION_SELECTED_SESSION_TARGETS_HEADING}\n${JSON.stringify(
            targets.map((target) => ({
                harnessId: target.harnessId,
                model: target.modelId,
                ...(target.thinking ? { thinking: target.thinking } : {}),
                label: action.name,
            }))
        )}`,
    }
}

function queuedSavedActionAdditionalInput(queuedTurn: QueuedTurn): UserInputContext | null {
    if (!queuedTurn.label?.endsWith(ACTION_ADDITIONAL_INSTRUCTIONS_LABEL_SUFFIX)) return null

    const marker = `\n\n${ACTION_ADDITIONAL_INSTRUCTIONS_HEADING}\n`
    const markerIndex = queuedTurn.input.lastIndexOf(marker)
    const images = queuedTurn.images ?? []

    return {
        userInput: markerIndex === -1 ? "" : queuedTurn.input.slice(markerIndex + marker.length),
        images,
    }
}

function mergeAdditionalInput(left: UserInputContext | null, right: UserInputContext | null): UserInputContext | null {
    if (!left) return right
    if (!right) return left

    const images = [...left.images]
    const imageIds = new Set(images.map((image) => image.id))
    for (const image of right.images) {
        if (imageIds.has(image.id)) continue
        imageIds.add(image.id)
        images.push(image)
    }

    return {
        userInput: [left.userInput.trim(), right.userInput.trim()].filter(Boolean).join("\n\n"),
        images,
    }
}

interface QueuedSavedActionReplacement {
    turn: QueuedTurn
    actionCount: number
}

interface PendingSavedActionBatch {
    actions: SavedAction[]
    additionalInput: UserInputContext | null
    claimedAdditionalInput: UserInputContext | null
    replacement: QueuedSavedActionReplacement | null
    queuedTurnId: string
    createdAt: string
    completion: Promise<void>
    resolve: () => void
    reject: (error: unknown) => void
    timer: ReturnType<typeof setTimeout>
}

interface RuntimeTurnOptions {
    clientRequestId?: string
    includeComments?: boolean
    label?: string
    comments?: Comment[]
    queuedInput?: UserInputContext
    turnInput?: TurnInput
}

export interface CommandStyle {
    variant?: "primary" | "success" | "danger" | "neutral" | "ghost"
}

export interface Command {
    id: string
    label: string
    tooltip?: string
    icon: LucideIcon
    order: number
    style?: CommandStyle
    action: () => Promise<void> | void
    show: boolean
    enabled: boolean
    spacer?: boolean
}

export type InputAction = SavedAction & { isAvailable: boolean }

/**
 * Owns the existing-task composer transport and saved Action dispatch.
 *
 * Product workflow is deliberately absent here. Do/Queue/Steer/Interrupt/Stop are
 * transport controls; everything opinionated is an editable, queueable Action.
 */
export class InputManager {
    private readonly taskId: string
    private readonly cancellingQueuedTurnIds = new Set<string>()
    private actionSubmissionChain: Promise<void> = Promise.resolve()
    private actionBatchesAwaitingSubmission = 0
    private pendingSavedActionBatch: PendingSavedActionBatch | null = null

    constructor(
        private readonly store: CodeStore,
        taskId: string,
        private readonly editorManager: SmartEditorManager
    ) {
        this.taskId = taskId
        makeAutoObservable<InputManager, "actionBatchesAwaitingSubmission" | "actionSubmissionChain" | "cancellingQueuedTurnIds" | "pendingSavedActionBatch">(
            this,
            {
                actionBatchesAwaitingSubmission: false,
                actionSubmissionChain: false,
                cancellingQueuedTurnIds: false,
                pendingSavedActionBatch: false,
            }
        )
    }

    private get taskModel() {
        return this.store.tasks.getTaskModel(this.taskId)
    }

    get isWorking(): boolean {
        return this.store.isTaskRunning(this.taskId)
    }

    private get hasInput(): boolean {
        return this.editorManager.value.trim().length > 0 || this.editorManager.pendingImages.length > 0
    }

    private get hasUnsubmittedComments(): boolean {
        return this.store.comments.getPendingCommentCount(this.taskId) > 0
    }

    private get hasFeedback(): boolean {
        return this.hasInput || this.hasUnsubmittedComments
    }

    private get isClosed(): boolean {
        return this.store.tasks.getTask(this.taskId)?.closed ?? false
    }

    private async reopenForSubmission(): Promise<void> {
        if (this.isClosed) await this.store.tasks.setTaskClosed(this.taskId, false)
        if (this.store.tasks.getTask(this.taskId)?.attention?.sleepUntil) await this.store.tasks.wakeTask(this.taskId)
    }

    private get supportsTurnSteering(): boolean {
        const harnessId = this.taskModel?.harnessId
        return harnessId ? supportsSteeringForHarness(harnessId) : false
    }

    private get hasContinuationSession(): boolean {
        return this.taskModel?.primarySessionThreadReference != null
    }

    get canSteerQueuedTurns(): boolean {
        return this.isWorking && this.supportsTurnSteering && this.hasContinuationSession
    }

    get value(): string {
        return this.editorManager.value
    }

    get files(): string[] {
        return this.editorManager.files
    }

    get queuedTurns(): QueuedTurn[] {
        const storedTurns = this.taskModel?.queuedTurns ?? []
        const replacedTurnId = this.pendingSavedActionBatch?.replacement?.turn.id
        return this.store.queuedTurns
            .queuedForTask(this.taskId, storedTurns, this.taskModel?.primarySessionThreadReference?.sessionId)
            .filter((turn) => turn.id !== replacedTurnId)
            .map((turn) => {
                const actionIds = savedActionIdsFromClientRequestId(turn.clientRequestId)
                if (actionIds.length === 0) return turn

                const actionNames: string[] = []
                for (const actionId of actionIds) {
                    const action = this.store.savedActions?.getAction(actionId)
                    if (!action) return turn
                    actionNames.push(action.name)
                }
                const hasAdditionalInput =
                    turn.label?.endsWith(ACTION_ADDITIONAL_INSTRUCTIONS_LABEL_SUFFIX) === true ||
                    turn.input.includes(`\n\n${ACTION_ADDITIONAL_INSTRUCTIONS_HEADING}\n`)
                const label = savedActionBatchLabel(actionNames, hasAdditionalInput)
                return turn.label !== label ? { ...turn, label } : turn
            })
    }

    get scheduledTurns(): FollowupSchedule[] {
        return this.store.tasks.scheduledTurns.forTask(this.taskId)
    }

    async cancelScheduledTurn(schedule: FollowupSchedule): Promise<void> {
        await this.store.tasks.scheduledTurns.cancel(schedule)
    }

    async runScheduledTurn(schedule: FollowupSchedule): Promise<void> {
        await this.store.tasks.scheduledTurns.runNow(schedule)
    }

    get actions(): readonly InputAction[] {
        const taskModel = this.taskModel
        const actions = this.store.savedActions?.installedActions ?? []
        const state = {
            taskRunning: this.isWorking,
            gitRepository: taskModel?.gitStatus !== null && taskModel?.gitStatus !== undefined,
            gitWorkingChanges: taskModel?.hasWorkingChanges ?? false,
            gitUnpushedCommits: (taskModel?.aheadCount ?? 0) > 0,
        }
        return actions.flatMap((action) => {
            const availability = savedActionAvailability(action, state)
            return availability === "hidden"
                ? []
                : [
                      {
                          ...action,
                          isAvailable: availability === "available" && !taskModel?.handoffSubmitting && !taskModel?.pendingHandoff,
                      },
                  ]
        })
    }

    get pullRequestAutomation() {
        return this.taskModel?.pullRequestAutomation
    }

    get pullRequestAutomationDisabledReason(): string | undefined {
        const model = this.taskModel
        if (!model?.primarySessionThreadReference) return "This task has no resumable session."
        if (model.handoffSubmitting || model.pendingHandoff) return "Finish the agent handoff first."
        if (this.store.tasks.getTask(this.taskId)?.closed) return "Reopen this task to enable PR automation."
        return undefined
    }

    async setWatchPR(enabled: boolean): Promise<string | null> {
        return this.taskModel?.setWatchPR(enabled) ?? "This task is unavailable."
    }

    async retryPullRequestAutomation(): Promise<string | null> {
        return this.taskModel?.retryPullRequestAutomation() ?? "This task is unavailable."
    }

    setValue(value: string): void {
        this.editorManager.setValue(value)
    }

    clear(): void {
        this.editorManager.clear()
    }

    async cancelQueuedTurn(queuedTurnId: string): Promise<void> {
        const pendingBatch = this.pendingSavedActionBatch
        if (pendingBatch?.queuedTurnId === queuedTurnId) {
            clearTimeout(pendingBatch.timer)
            this.pendingSavedActionBatch = null
            this.suppressPendingSavedActionBatch(pendingBatch)
            try {
                await this.restorePendingSavedActionInput(pendingBatch.claimedAdditionalInput)
                pendingBatch.resolve()
            } catch (error) {
                pendingBatch.reject(error)
                throw error
            }
            return
        }

        const queuedTurn = this.queuedTurns.find((turn) => turn.id === queuedTurnId)
        if (!queuedTurn) return
        const cancelled = await this.cancelQueuedTurnRecord(queuedTurn)
        if (!cancelled) return

        await this.recoverCancelledQueuedTurn(queuedTurn)
    }

    async steerQueuedTurn(queuedTurnId: string): Promise<boolean> {
        if (!this.canSteerQueuedTurns) return false

        const pendingBatch = this.pendingSavedActionBatch
        if (pendingBatch?.queuedTurnId === queuedTurnId) {
            this.flushPendingSavedActionBatch("steer")
            await pendingBatch.completion
            return true
        }

        const queuedTurn = this.queuedTurns.find((turn) => turn.id === queuedTurnId)
        if (!queuedTurn) return false
        const cancelled = await this.cancelQueuedTurnRecord(queuedTurn)
        if (!cancelled) return false

        const input: UserInputContext = {
            userInput: queuedTurn.input,
            images: queuedTurn.images ?? [],
        }
        const clientRequestId = queuedTurn.clientRequestId ?? `${this.taskId}:composer:${ulid()}`
        let accepted = false
        try {
            const result = await this.executeRuntimeTurn(
                input,
                "steer",
                {
                    clientRequestId,
                    includeComments: queuedTurn.includeComments,
                    label: queuedTurn.label ?? "Steer",
                    queuedInput: input,
                    turnInput: queuedTurn.turnInput,
                },
                () => {
                    accepted = true
                }
            )
            try {
                await this.store.comments.rebindCommentsForTurn({
                    taskId: this.taskId,
                    previousTurnId: queuedTurn.id,
                    submission: {
                        clientRequestId,
                        sessionId: result.session.sessionId,
                        turnId: result.turn.turnId,
                        submittedAt: result.turn.completedAt ?? result.turn.startedAt ?? result.turn.queuedAt ?? new Date().toISOString(),
                    },
                })
            } catch (commentError) {
                console.error("[InputManager] Queued message was steered, but its included comments could not be rebound", {
                    error: commentError,
                    taskId: this.taskId,
                    turnId: result.turn.turnId,
                })
            }
            const savedActionIds = savedActionIdsFromClientRequestId(queuedTurn.clientRequestId)
            if (savedActionIds.length > 0) {
                this.store.tasks.rebindSavedActionRun({
                    taskId: this.taskId,
                    clientRequestId,
                    sessionId: result.session.sessionId,
                    turnId: result.turn.turnId,
                })
            }
            const workspaceId = this.taskModel?.repoId
            if (workspaceId) {
                const fromEntryId = savedActionIds.length > 0 ? `action.${queuedTurn.id}` : `do.${clientRequestId}`
                const toEntryId = savedActionIds.length > 0 && result.effectiveDisposition !== "steered" ? `action.${result.turn.turnId}` : fromEntryId
                void this.store.timelines
                    .moveInvocation({
                        taskId: this.taskId,
                        workspaceId,
                        fromEntryId,
                        toEntryId,
                        anchor: {
                            sessionId: result.session.sessionId,
                            turnId: result.turn.turnId,
                        },
                    })
                    .catch((timelineError) => {
                        console.warn("[InputManager] Queued message was steered, but its task timeline anchor could not be updated", {
                            error: timelineError,
                            taskId: this.taskId,
                            turnId: result.turn.turnId,
                        })
                    })
            }
            return true
        } catch (error) {
            if (!accepted) {
                try {
                    await this.recoverCancelledQueuedTurn(queuedTurn)
                } catch (recoveryError) {
                    console.error("[InputManager] Failed to restore a queued message after steering failed", {
                        error: recoveryError,
                        taskId: this.taskId,
                        turnId: queuedTurn.id,
                    })
                }
            }
            throw error
        }
    }

    private async recoverCancelledQueuedTurn(queuedTurn: QueuedTurn): Promise<void> {
        const workspaceId = this.taskModel?.repoId
        await this.store.comments.restoreCommentsForTurn(this.taskId, queuedTurn.id)

        const savedActionIds = savedActionIdsFromClientRequestId(queuedTurn.clientRequestId)
        if (savedActionIds.length === 0) {
            if (workspaceId && queuedTurn.clientRequestId) {
                try {
                    await this.store.timelines.removeDo({
                        taskId: this.taskId,
                        workspaceId,
                        clientRequestId: queuedTurn.clientRequestId,
                    })
                } catch (error) {
                    console.warn("[InputManager] Manual turn was cancelled, but its task timeline entry could not be removed", {
                        taskId: this.taskId,
                        turnId: queuedTurn.id,
                        error,
                    })
                }
            }
            await this.editorManager.restoreCancelledInput({
                userInput: queuedTurn.input,
                images: queuedTurn.images ?? [],
            })
            return
        }

        if (workspaceId) {
            try {
                await this.store.timelines.removeAction({ taskId: this.taskId, workspaceId, turnId: queuedTurn.id })
            } catch (error) {
                console.warn("[InputManager] Action was cancelled, but its task timeline entry could not be removed", {
                    taskId: this.taskId,
                    turnId: queuedTurn.id,
                    error,
                })
            }
        }

        const additionalInput = queuedSavedActionAdditionalInput(queuedTurn)
        if (additionalInput) await this.editorManager.restoreCancelledInput(additionalInput)
    }

    get commands(): Command[] {
        const commands: Command[] = [
            {
                id: "do",
                label: "Do",
                icon: Play,
                order: 0,
                style: { variant: "success" },
                show: !this.isWorking,
                enabled: this.hasFeedback,
                action: () => this.submitComposer("enqueue", "Do"),
            },
            {
                id: "queue",
                label: "Queue",
                icon: ListPlus,
                order: 0,
                style: { variant: "success" },
                show: this.isWorking && this.hasContinuationSession,
                enabled: this.hasFeedback,
                action: () => this.submitComposer("enqueue", "Queue"),
            },
            {
                id: "steer",
                label: "Steer",
                tooltip: "Sends feedback to the active turn. Delivery does not guarantee the model follows the update.",
                icon: Route,
                order: 1,
                style: { variant: "neutral" },
                show: this.isWorking && this.supportsTurnSteering && this.hasContinuationSession,
                enabled: this.hasFeedback,
                action: () => this.submitComposer("steer", "Steer"),
            },
            {
                id: "interrupt",
                label: "Interrupt",
                tooltip: "Stop the active turn and send this message next.",
                icon: Zap,
                order: 2,
                style: { variant: "danger" },
                show: this.isWorking && this.hasContinuationSession,
                enabled: this.hasFeedback,
                action: () => this.submitComposer("interrupt", "Interrupt"),
            },
            {
                id: "stop",
                label: "Stop",
                tooltip: "Stop the active turn without sending another message.",
                icon: Square,
                order: 3,
                style: { variant: "danger" },
                show: this.isWorking,
                enabled: true,
                action: () => this.store.queries.abortTask(this.taskId),
            },
            {
                id: "reopen",
                label: "Reopen",
                icon: RotateCcw,
                order: 0,
                style: { variant: "primary" },
                show: this.isClosed,
                enabled: true,
                spacer: true,
                action: async () => {
                    await this.store.tasks.setTaskClosed(this.taskId, false)
                },
            },
        ]

        return commands
            .filter((command) => command.show)
            .map((command) => ({
                ...command,
                enabled: command.enabled && (command.id === "stop" || (!this.taskModel?.handoffSubmitting && !this.taskModel?.pendingHandoff)),
            }))
            .sort((left, right) => left.order - right.order)
    }

    /** Colleagues' pending suggestions for this task, newest last. */
    get pendingProposals(): TaskProposal[] {
        return this.store.proposals.pendingFor(this.taskId)
    }

    /**
     * Runs a colleague's suggestion as an ordinary turn from this client's
     * own principal, then marks the proposal approved with the accepted turn.
     */
    async approveProposal(proposalId: string): Promise<void> {
        const proposal = this.pendingProposals.find((candidate) => candidate.id === proposalId)
        const workspaceId = this.taskModel?.repoId
        if (!proposal || !workspaceId) throw new Error("This suggestion is no longer pending")
        const clientRequestId = `${this.taskId}:proposal:${proposal.id}`
        const input: UserInputContext = { userInput: proposal.input, images: [] }
        const submittedAt = new Date().toISOString()
        const result = await this.executeRuntimeTurn(input, "enqueue", {
            clientRequestId,
            label: `${proposal.by.displayName} suggested`,
            queuedInput: input,
        })
        await this.store.proposals.resolve(this.taskId, workspaceId, proposal.id, "approved", { turnId: result.turn.turnId })
        void this.store.timelines
            .appendDo({
                taskId: this.taskId,
                workspaceId,
                id: `do.${clientRequestId}`,
                commentCount: 0,
                createdAt: submittedAt,
                anchor: { sessionId: result.session.sessionId, turnId: result.turn.turnId },
            })
            .catch((error) => {
                console.warn("[InputManager] Approved suggestion was accepted, but its task timeline entry could not be recorded", {
                    taskId: this.taskId,
                    turnId: result.turn.turnId,
                    error,
                })
            })
    }

    async declineProposal(proposalId: string, note?: string): Promise<void> {
        const workspaceId = this.taskModel?.repoId
        if (!workspaceId) throw new Error("This task is no longer available")
        await this.store.proposals.resolve(this.taskId, workspaceId, proposalId, "declined", note ? { note } : {})
    }

    async runCommand(id: string): Promise<void> {
        const command = this.commands.find((candidate) => candidate.id === id)
        if (!command?.enabled) return
        track("command_run", { commandType: id })
        await command.action()
    }

    async listSessionActionHarnesses(): Promise<WildLarkTaskHarness[]> {
        const taskModel = this.taskModel
        if (!taskModel?.repoId) throw new Error("This task is no longer available")
        const sessionId = taskModel.primarySessionThreadReference?.sessionId
        if (!sessionId) throw new Error("This task does not have a Nest session yet")
        const result = await requestWildLarkMethod("wildlark.task.harnesses.list", {
            taskId: this.taskId,
            workspaceId: taskModel.repoId,
            sessionId,
        })
        return result.harnesses
    }

    async runSavedActions(actionIds: readonly string[], options: SavedActionRunOptions = {}): Promise<PromiseSettledResult<void>[]> {
        const submissions = actionIds.map((id) => this.runSavedAction(id, options))
        // The full selection is known; only individual clicks need the coalesce window.
        this.flushPendingSavedActionBatch()
        return Promise.allSettled(submissions)
    }

    async runSavedAction(actionId: string, options: SavedActionRunOptions = {}): Promise<void> {
        const action = this.actions.find((candidate) => candidate.id === actionId)
        if (!action) throw new Error("Action is unavailable")
        if (!action.isAvailable) throw new Error(`Action "${action.name}" is unavailable`)
        if (action.requiresConfirmation && options.confirmed !== true) {
            throw new Error(`Action "${action.name}" requires confirmation`)
        }

        await this.enqueueSavedAction(savedActionWithSessionTargets(action, options.sessionTargets))
    }

    private enqueueSavedAction(action: SavedAction): Promise<void> {
        if (!action.allowCombining) {
            if (this.pendingSavedActionBatch) this.flushPendingSavedActionBatch()
            const claimedAdditionalInput = this.hasInput ? this.captureSnapshotAndClear().input : null
            const completion = this.startPendingSavedActionBatch([action], claimedAdditionalInput, claimedAdditionalInput, null)
            this.flushPendingSavedActionBatch()
            return completion
        }

        const pending = this.pendingSavedActionBatch
        if (pending) {
            const combinedPromptLength = savedActionBatchPrompt([...pending.actions, action], pending.additionalInput).length
            if (pending.actions.length < MAX_COMBINED_ACTIONS && combinedPromptLength <= MAX_COMBINED_ACTION_PROMPT_LENGTH) {
                pending.actions.push(action)
                this.resetPendingSavedActionBatchTimer(pending)
                this.showPendingSavedActionBatch(pending)
                return pending.completion
            }
            this.flushPendingSavedActionBatch()
        }

        const claimedAdditionalInput = this.hasInput ? this.captureSnapshotAndClear().input : null
        const replacement = this.queuedSavedActionReplacement([action], claimedAdditionalInput)
        if (replacement) {
            return this.startPendingSavedActionBatch(
                [...replacement.actions, action],
                mergeAdditionalInput(replacement.additionalInput, claimedAdditionalInput),
                claimedAdditionalInput,
                {
                    turn: replacement.turn,
                    actionCount: replacement.actions.length,
                }
            )
        }

        return this.startPendingSavedActionBatch([action], claimedAdditionalInput, claimedAdditionalInput, null)
    }

    private queuedSavedActionReplacement(
        newActions: readonly SavedAction[],
        claimedAdditionalInput: UserInputContext | null,
        options: { allowWhileSubmissionPending?: boolean } = {}
    ): {
        turn: QueuedTurn
        actions: SavedAction[]
        additionalInput: UserInputContext | null
    } | null {
        if (newActions.some((action) => !action.allowCombining)) return null
        if (!this.isWorking || (this.actionBatchesAwaitingSubmission > 0 && options.allowWhileSubmissionPending !== true)) return null
        const turn = this.queuedTurns.at(-1)
        if (!turn) return null

        const actionIds = savedActionIdsFromClientRequestId(turn.clientRequestId)
        if (actionIds.length === 0 || actionIds.length + newActions.length > MAX_COMBINED_ACTIONS) return null

        const actions: SavedAction[] = []
        for (const actionId of actionIds) {
            const queuedAction = this.store.savedActions?.getAction(actionId)
            if (!queuedAction?.allowCombining) return null
            actions.push(queuedAction)
        }

        const additionalInput = queuedSavedActionAdditionalInput(turn)
        const promptLength = savedActionBatchPrompt([...actions, ...newActions], mergeAdditionalInput(additionalInput, claimedAdditionalInput)).length
        return promptLength <= MAX_COMBINED_ACTION_PROMPT_LENGTH ? { turn, actions, additionalInput } : null
    }

    private startPendingSavedActionBatch(
        actions: SavedAction[],
        additionalInput: UserInputContext | null,
        claimedAdditionalInput: UserInputContext | null,
        replacement: QueuedSavedActionReplacement | null
    ): Promise<void> {
        let resolve: PendingSavedActionBatch["resolve"] = () => undefined
        let reject: PendingSavedActionBatch["reject"] = () => undefined
        const completion = new Promise<void>((resolvePromise, rejectPromise) => {
            resolve = resolvePromise
            reject = rejectPromise
        })
        const createdAt = new Date().toISOString()
        const batch: PendingSavedActionBatch = {
            actions,
            additionalInput,
            claimedAdditionalInput,
            replacement,
            queuedTurnId: `pending-action:${ulid()}`,
            createdAt,
            completion,
            resolve,
            reject,
            timer: setTimeout(() => {
                if (this.pendingSavedActionBatch === batch) this.flushPendingSavedActionBatch()
            }, ACTION_COALESCE_WINDOW_MS),
        }
        this.pendingSavedActionBatch = batch
        this.showPendingSavedActionBatch(batch)
        return completion
    }

    private resetPendingSavedActionBatchTimer(batch: PendingSavedActionBatch): void {
        clearTimeout(batch.timer)
        batch.timer = setTimeout(() => {
            if (this.pendingSavedActionBatch === batch) this.flushPendingSavedActionBatch()
        }, ACTION_COALESCE_WINDOW_MS)
    }

    private showPendingSavedActionBatch(batch: PendingSavedActionBatch): void {
        this.store.queuedTurns.acceptQueuedTurn(this.taskId, {
            id: batch.queuedTurnId,
            sessionId: this.taskModel?.primarySessionThreadReference?.sessionId,
            type: "do",
            input: savedActionBatchPrompt(batch.actions, batch.additionalInput),
            images: batch.additionalInput?.images ?? [],
            status: "queued",
            createdAt: batch.createdAt,
            updatedAt: new Date().toISOString(),
            label: savedActionBatchLabel(
                batch.actions.map((action) => action.name),
                batch.additionalInput !== null
            ),
            note: "Combining for 2.3s…",
            includeComments: false,
        })
    }

    private suppressPendingSavedActionBatch(batch: PendingSavedActionBatch): void {
        this.store.queuedTurns.suppressQueuedTurn(this.taskId, batch.queuedTurnId, this.taskModel?.queuedTurns ?? [])
    }

    private async restorePendingSavedActionInput(input: UserInputContext | null): Promise<void> {
        if (!input) return
        await this.editorManager.restoreCancelledInput(input)
    }

    private flushPendingSavedActionBatch(disposition: TurnDisposition = "enqueue"): void {
        const batch = this.pendingSavedActionBatch
        if (!batch) return
        clearTimeout(batch.timer)
        this.pendingSavedActionBatch = null
        this.suppressPendingSavedActionBatch(batch)

        this.actionBatchesAwaitingSubmission += 1
        const submission = this.actionSubmissionChain.then(() => this.submitSavedActionBatch(batch, disposition))
        this.actionSubmissionChain = submission.catch(() => undefined)
        void submission.then(
            () => {
                this.actionBatchesAwaitingSubmission -= 1
                batch.resolve()
            },
            (error) => {
                this.actionBatchesAwaitingSubmission -= 1
                batch.reject(error)
            }
        )
    }

    private async submitSavedActionBatch(batch: PendingSavedActionBatch, disposition: TurnDisposition): Promise<void> {
        let actions = batch.actions
        let additionalInput = batch.additionalInput
        let inputToRestore = batch.claimedAdditionalInput
        const trackedActions = batch.replacement ? batch.actions.slice(batch.replacement.actionCount) : batch.actions
        let replacement = batch.replacement
        let replacedTimelineEntryId: string | undefined
        if (!replacement) {
            const queuedReplacement = this.queuedSavedActionReplacement(actions, additionalInput, {
                allowWhileSubmissionPending: true,
            })
            if (queuedReplacement) {
                replacement = {
                    turn: queuedReplacement.turn,
                    actionCount: queuedReplacement.actions.length,
                }
                actions = [...queuedReplacement.actions, ...actions]
                additionalInput = mergeAdditionalInput(queuedReplacement.additionalInput, additionalInput)
            }
        }
        if (replacement) {
            let replacementCancelled = false
            try {
                replacementCancelled = await this.cancelQueuedTurnRecord(replacement.turn)
            } catch (error) {
                try {
                    await this.restorePendingSavedActionInput(batch.claimedAdditionalInput)
                } catch (restoreError) {
                    console.error("[InputManager] Failed to restore Action instructions after queue merge failure", {
                        error: restoreError,
                        taskId: this.taskId,
                    })
                }
                throw error
            }
            if (replacementCancelled) {
                inputToRestore = additionalInput
                replacedTimelineEntryId = `action.${replacement.turn.id}`
            } else {
                actions = actions.slice(replacement.actionCount)
                additionalInput = batch.claimedAdditionalInput
            }
        }

        const firstAction = actions[0]
        if (!firstAction) return
        const actionIds = actions.map((action) => action.id)
        const input: UserInputContext = {
            userInput: savedActionBatchPrompt(actions, additionalInput),
            images: additionalInput?.images ?? [],
        }
        const label = savedActionBatchLabel(
            actions.map((action) => action.name),
            additionalInput !== null
        )
        const requestId = ulid()
        const clientRequestId =
            actionIds.length === 1
                ? savedActionClientRequestId(this.taskId, firstAction.id, requestId)
                : combinedSavedActionClientRequestId(this.taskId, actionIds, requestId)
        const workspaceId = this.taskModel?.repoId
        if (!workspaceId) throw new Error("This task is no longer available")
        let accepted = false
        try {
            const result = await this.executeRuntimeTurn(
                input,
                disposition,
                {
                    clientRequestId,
                    includeComments: false,
                    label,
                },
                () => {
                    accepted = true
                }
            )
            this.store.tasks.recordSavedActionRun({
                taskId: this.taskId,
                sessionId: result.session.sessionId,
                turnId: result.turn.turnId,
                clientRequestId,
                input: input.userInput,
                actions: actions.map(({ id, name, icon, color }) => ({ id, name, icon, color })),
                createdAt: result.turn.queuedAt ?? result.turn.startedAt ?? new Date().toISOString(),
            })
            void this.store.timelines
                .appendAction({
                    taskId: this.taskId,
                    workspaceId,
                    id: `action.${result.turn.turnId}`,
                    actions,
                    withInstructions: additionalInput !== null,
                    createdAt: result.turn.completedAt ?? result.turn.startedAt ?? result.turn.queuedAt ?? new Date().toISOString(),
                    anchor: {
                        sessionId: result.session.sessionId,
                        turnId: result.turn.turnId,
                    },
                    ...(replacedTimelineEntryId ? { replacesEntryId: replacedTimelineEntryId } : {}),
                })
                .catch((error) => {
                    console.warn("[InputManager] Action was accepted, but its task timeline entry could not be recorded", {
                        taskId: this.taskId,
                        turnId: result.turn.turnId,
                        error,
                    })
                })
        } catch (error) {
            if (!accepted) {
                try {
                    await this.restorePendingSavedActionInput(inputToRestore)
                } catch (restoreError) {
                    console.error("[InputManager] Failed to restore Action instructions after submission failure", {
                        error: restoreError,
                        taskId: this.taskId,
                    })
                }
            }
            throw error
        }
        for (const action of trackedActions) track("action_run", { actionId: action.id })
    }

    private async cancelQueuedTurnRecord(queuedTurn: QueuedTurn): Promise<boolean> {
        const taskModel = this.taskModel
        if (!taskModel || this.cancellingQueuedTurnIds.has(queuedTurn.id)) return false

        this.cancellingQueuedTurnIds.add(queuedTurn.id)
        try {
            const cancelled = await taskModel.cancelQueuedTurn(queuedTurn.id)
            if (!cancelled) return false
            this.store.queuedTurns.suppressQueuedTurn(this.taskId, queuedTurn.id, taskModel.queuedTurns)
            return true
        } finally {
            this.cancellingQueuedTurnIds.delete(queuedTurn.id)
        }
    }

    private async submitComposer(disposition: TurnDisposition, label: string): Promise<void> {
        const { input: composerInput, snapshot } = this.captureSnapshotAndClear()
        const comments = this.store.comments.getUnsubmittedComments(this.taskId)
        const input = withInlineComments(composerInput, comments)
        const clientRequestId = `${this.taskId}:composer:${ulid()}`
        const submittedAt = new Date().toISOString()
        let accepted = false
        try {
            const result = await this.executeRuntimeTurn(
                input,
                disposition,
                {
                    clientRequestId,
                    label,
                    includeComments: comments.length > 0,
                    comments,
                    queuedInput: composerInput,
                },
                () => {
                    accepted = true
                }
            )
            const workspaceId = this.taskModel?.repoId
            if (workspaceId) {
                void this.store.timelines
                    .appendDo({
                        taskId: this.taskId,
                        workspaceId,
                        id: `do.${clientRequestId}`,
                        commentCount: comments.length,
                        createdAt: submittedAt,
                        anchor: {
                            sessionId: result.session.sessionId,
                            turnId: result.turn.turnId,
                        },
                    })
                    .catch((error) => {
                        console.warn("[InputManager] Manual turn was accepted, but its task timeline entry could not be recorded", {
                            taskId: this.taskId,
                            turnId: result.turn.turnId,
                            error,
                        })
                    })
            }
        } catch (error) {
            if (!accepted) this.editorManager.restoreSnapshot(snapshot)
            throw error
        }
    }

    private captureSnapshotAndClear(): {
        input: UserInputContext
        snapshot: EditorSnapshot
    } {
        const snapshot = this.editorManager.captureSnapshot()
        const input = {
            userInput: snapshot.value.trim(),
            images: [...snapshot.pendingImages],
        }
        this.editorManager.clear({ revokeImagePreviews: false })
        return { input, snapshot }
    }

    private async executeRuntimeTurn(
        input: UserInputContext,
        disposition: TurnDisposition,
        options: RuntimeTurnOptions,
        onAccepted?: () => void
    ): Promise<SessionTurnStartResult> {
        const taskModel = this.taskModel
        if (!taskModel?.repoId) throw new Error("This task is no longer available")
        if (taskModel.handoffSubmitting || taskModel.pendingHandoff) throw new Error("Finish the pending handoff before sending more work")
        this.assertNestTurnSupported(input, taskModel.enabledMcpServerIds)
        const sessionId = taskModel.primarySessionThreadReference?.sessionId
        if (!sessionId) throw new Error("This task does not have a Nest session yet")

        await this.reopenForSubmission()

        const clientRequestId = options.clientRequestId ?? `${this.taskId}:composer:${ulid()}`
        const turnInput = options.turnInput ?? userInputTurnInput(input)
        const thinking = taskModel.thinking
        const params: WildLarkTaskTurnStartParams = {
            taskId: this.taskId,
            workspaceId: taskModel.repoId,
            sessionId,
            input: turnInput,
            disposition,
            clientRequestId,
            options: {
                mode: "full",
                permissionMode: this.store.personalSettingsStore?.settings.current.permissionMode ?? "yolo",
                model: taskModel.model,
                providerOptions: {
                    thinking,
                    fastMode: taskModel.fastMode,
                },
            },
        }
        taskModel.beginModelSubmission(clientRequestId)
        let result: SessionTurnStartResult
        try {
            result = await requestWildLarkMethod("wildlark.task.turn.start", params)
        } catch (error) {
            taskModel.rejectModelSubmission(clientRequestId)
            throw error
        }
        if (result.effectiveDisposition === "steered") {
            taskModel.rejectModelSubmission(clientRequestId)
        } else {
            taskModel.acceptModelSubmission(clientRequestId, result.turn.turnId, result.turn.status)
        }
        onAccepted?.()
        this.store.tasks.recordNestSessionActivity({
            taskId: this.taskId,
            sessionId: result.session.sessionId,
            ...(result.effectiveDisposition !== "steered" ? { thinking } : {}),
            activityAt: result.turn.completedAt ?? result.turn.startedAt ?? result.turn.queuedAt ?? new Date().toISOString(),
        })
        this.store.runtimes.applyTurnUpdate(this.taskId, {
            turnId: result.turn.turnId,
            sessionId: result.session.sessionId,
            status: result.turn.status,
            queuedAt: result.turn.queuedAt,
            startedAt: result.turn.startedAt,
            completedAt: result.turn.completedAt,
            error: result.turn.error,
        })
        if (result.effectiveDisposition === "steered") {
            acceptOptimisticSteeredInput({
                sessionId: result.session.sessionId,
                turnId: result.turn.turnId,
                input: turnInput,
                clientRequestId,
                receivedAt: new Date().toISOString(),
            })
        }
        this.rememberQueuedTurn(result, input, taskModel, options, disposition, clientRequestId)
        if (options.comments && options.comments.length > 0) {
            try {
                await this.store.comments.markCommentsSubmitted({
                    taskId: this.taskId,
                    commentIds: options.comments.map((comment) => comment.id),
                    submission: {
                        clientRequestId,
                        sessionId: result.session.sessionId,
                        turnId: result.turn.turnId,
                        submittedAt: result.turn.completedAt ?? result.turn.startedAt ?? result.turn.queuedAt ?? new Date().toISOString(),
                    },
                })
            } catch (error) {
                console.error("[InputManager] Turn was accepted, but its included comments could not be marked submitted", {
                    taskId: this.taskId,
                    turnId: result.turn.turnId,
                    error,
                })
            }
        }
        return result
    }

    private rememberQueuedTurn(
        result: SessionTurnStartResult,
        input: UserInputContext,
        taskModel: {
            enabledMcpServerIds: string[]
            harnessId: string
            model: string
            thinking: QueuedTurn["thinking"]
            fastMode: boolean
        },
        options: RuntimeTurnOptions,
        disposition: TurnDisposition,
        clientRequestId: string
    ): void {
        if (result.effectiveDisposition !== "enqueued") return

        const now = new Date().toISOString()
        const queuedInput = options.queuedInput ?? input
        const fallbackNote = disposition === "steer" && result.effectiveDisposition === "enqueued" ? "will send when the current step finishes" : undefined
        const commentCount = options.comments?.length ?? 0
        const commentNote = commentCount === 0 ? undefined : commentCount === 1 ? "1 comment" : `${commentCount} comments`
        const note = [fallbackNote, commentNote].filter(Boolean).join(" · ")
        this.store.queuedTurns.acceptQueuedTurn(this.taskId, {
            id: result.turn.turnId,
            sessionId: result.session.sessionId,
            type: "do",
            input: queuedInput.userInput,
            images: queuedInput.images,
            turnInput: options.turnInput ?? userInputTurnInput(input),
            status: "queued",
            createdAt: now,
            updatedAt: now,
            clientRequestId,
            enabledMcpServerIds: taskModel.enabledMcpServerIds,
            harnessId: taskModel.harnessId,
            modelId: taskModel.model,
            label: options.label ?? "Queue",
            includeComments: options.includeComments,
            thinking: taskModel.thinking,
            fastMode: taskModel.fastMode,
            ...(note ? { note } : {}),
        })
    }

    private assertNestTurnSupported(input: UserInputContext, enabledMcpServerIds: string[]): void {
        const blockedFeature = blockedFeatureForTurn({
            input,
            enabledMcpServerIds,
        })
        if (blockedFeature) throw new Error(describeBlockedFeature(blockedFeature))
    }
}
